import React from 'react';
import { Tag, Typography, Progress, Space, Button } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import type { RagStatus } from '@/services/ownerIntelligence.service';
import { SignalBadge, statusColor } from './oi-signals-ui';
import { scoreToSignalTier, signalTierColor } from './oi-signals';

const { Text } = Typography;

export { SignalBadge, RagTag, ConfidenceBadge, ConfidenceDots, DataCoverageDots, statusColor } from './oi-signals-ui';
export { HeroKpiCard, PrimaryKpiCard, SecondaryKpiCard, MicroKpiCard } from './oi-kpi-cards';
export { DeltaBadge } from './oi-delta';
export { OiEmptyState } from './oi-empty-state';
export { OiPageFrame } from './oi-page-frame';
export { OiMetricHelp } from './oi-metric-help';
export { OiDetailDrawer, ProportionalBar } from './OiDetailDrawer';
export {
  OiDetailJsonPanel,
  OiExecutiveTablePanel,
  OiKpiDrillDownPanel,
  OiCashChasePanel,
  OiRiskRowPanel,
  OiSupplierPanel,
} from './oi-executive-detail';

/** @deprecated Use HeroKpiCard */
export function HealthScoreCard({
  score,
  status,
  label,
  sublabel,
}: {
  score: number;
  status: RagStatus;
  label: string;
  sublabel?: string;
}) {
  const tier = status === 'green' ? scoreToSignalTier(score) : status === 'amber' ? 'watch' : 'at_risk';
  return (
    <div className="oi-health-card">
      <div className="oi-kpi-card__accent" style={{ background: signalTierColor(tier) }} />
      <Text type="secondary">{label}</Text>
      <div className="oi-kpi-card__value oi-kpi-card__value--primary oi-mono-value" style={{ color: statusColor(status) }}>
        {score}%
      </div>
      <SignalBadge tier={tier} />
      {sublabel && (
        <div style={{ marginTop: 8 }}>
          <Text type="secondary">{sublabel}</Text>
        </div>
      )}
      <Progress percent={score} showInfo={false} strokeColor={statusColor(status)} style={{ marginTop: 12 }} />
    </div>
  );
}

export function WorryAboutTodayPanel({
  items,
  onItemAction,
}: {
  items: Array<{ area: string; metric: string; count: number; severity: string; message: string; link?: string }>;
  onItemAction?: (link: string) => void;
}) {
  if (!items.length) {
    return <Text type="secondary">No critical exceptions detected today.</Text>;
  }
  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      {items.map((item, i) => (
        <div
          key={i}
          className={`oi-worry-item oi-worry-item--${item.severity === 'critical' ? 'critical' : item.severity === 'warning' ? 'warning' : ''}`}
        >
          <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
            <Space>
              <Tag color={item.severity === 'critical' ? 'red' : item.severity === 'warning' ? 'orange' : 'blue'}>
                {item.area}
              </Tag>
              <Text strong className="oi-worry-item__message">{item.message}</Text>
              {item.count > 0 ? <Tag>{item.count}</Tag> : null}
            </Space>
            {item.link && onItemAction ? (
              <Button type="link" size="small" icon={<ArrowRightOutlined />} onClick={() => onItemAction(item.link!)}>
                Review
              </Button>
            ) : null}
          </Space>
        </div>
      ))}
    </Space>
  );
}

export function InsightActionCard({
  title,
  description,
  priority,
  onAction,
}: {
  title: string;
  description: string;
  priority: string;
  onAction?: () => void;
}) {
  const priorityColor =
    priority === 'high' || priority === 'critical'
      ? 'var(--oi-crimson)'
      : priority === 'medium'
        ? 'var(--oi-amber)'
        : 'var(--oi-text-muted)';

  return (
    <div className="oi-insight-card">
      <div className="oi-kpi-card__accent" style={{ background: priorityColor }} />
      <Space direction="vertical" size={4}>
        <Space>
          <Text strong>{title}</Text>
          <Tag style={{ textTransform: 'uppercase' }}>{priority}</Tag>
        </Space>
        <Text type="secondary" className="oi-insight-card__description">{description}</Text>
        {onAction ? (
          <Button type="link" size="small" icon={<ArrowRightOutlined />} onClick={onAction} className="oi-ghost-action">
            Take action
          </Button>
        ) : null}
      </Space>
    </div>
  );
}

export function NarrativeBlock({ text }: { text: string }) {
  return (
    <div className="oi-narrative-block">
      <Text>{text}</Text>
    </div>
  );
}
